import React, { useEffect, useRef, useState } from "react";
import "assets/css/header.css";
import Logo from "assets/images/company_logo.png";
import { HiOutlineBell } from "react-icons/hi2";
import { Dropdown } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Settings, LogOut, MapPin, ChevronDown } from "lucide-react";
import LogoutModal from "components/common/LogoutModal";
import { logoutAndRedirect } from "utils/logout";
import NotificationsPreview from "components/common/NotificationsPreview";
import CurrentLocation from "components/common/CurrentLocation";
import { useSavedNotifications } from "hooks/useSavedNotifications";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { updateHeaderData } from "store/slices/uiSlice";
import { apiService } from "services/apiService";
import './Header.css';

interface HeaderProps {
  isDomainVerified: boolean;
}

const getPageTitle = (pathname: string): string => {
  const path = pathname.toLowerCase();
  
  if (path === '/' || path === '/dashboard') return 'Dashboard';
  if ((path.includes('/candidate/') || path.includes('/candidates/')) && path !== '/candidates') return 'Candidate Profile';
  if (path.includes('candidate') || path.includes('candidate')) return 'Candidates';
  if (path.includes('connection') || path.includes('matchingprofile')) return 'Connections';
  if (path.includes('setting')) return 'Settings';
  if (path.includes('notification')) return 'Notifications';
  if (path.includes('transaction') || path.includes('credit')) return 'Billing & Transactions';
  if (path.includes('job')) return 'Job Postings';
  if(path.includes('company-profile')) return 'Company Profile';
  if(path.includes('personal-profile')) return 'Personal Profile';
  if(path.includes('chat')) return 'Messages';
  if(path.includes('payment')) return 'Credit Purchase & Features';

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const raw = segments[0].replace(/-/g, ' ');
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  return 'Dashboard';
};

const Header: React.FC<HeaderProps> = ({ isDomainVerified }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  // Logout Handlers
  const handleLogout = () => {
    setShowModal(true);
  };

  const handleConfirmLogout = async () => {
    setLoading(true);
    try {
      await logoutAndRedirect("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      window.location.href = "/login";
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();

  // Use headerData from Redux as the single source of truth for Header display
  const { email, companyName, companyLogo } = useSelector((state: RootState) => state.ui.headerData);
  // Legacy selectors kept for backward-compat (e.g. direct logo updates from CompanyProfileView)
  const reduxProfileImage = useSelector((state: RootState) => state.ui.profileImageUrl);
  const reduxProfileName = useSelector((state: RootState) => state.ui.profileName);

  // Auto-fetch profile if headerData is missing fields after login or reload
  useEffect(() => {
    const fetchProfileIfNeeded = async () => {
      const hasToken =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token");
      if (!hasToken) return;

      if (!email || !companyName || !companyLogo) {
        try {
          const response: any = await apiService.getProfile();
          const detail = response?.data?.companeyDetail || response?.data || null;
          if (detail) {
            const fetchedEmail = detail.company_email || detail.email || detail.official_email || '';
            const fetchedCompanyName = detail.company_name || detail.name || '';
            const fetchedCompanyLogo = detail.logo_image_url || detail.company_img || detail.logo || '';
            if (fetchedEmail || fetchedCompanyName || fetchedCompanyLogo) {
              dispatch(
                updateHeaderData({
                  email: fetchedEmail,
                  companyName: fetchedCompanyName,
                  companyLogo: fetchedCompanyLogo,
                  source: 'Login',
                })
              );
            }
          }
        } catch (err) {
          console.warn('[Header] Failed to auto-fetch profile for headerData:', err);
        }
      }
    };
    fetchProfileIfNeeded();
  }, [email, companyName, companyLogo, dispatch]);

  const notificationRef = useRef<HTMLDivElement | null>(null);
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    refresh: refreshNotifications,
    markRead,
  } = useSavedNotifications({ pageSize: 10 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derive display values — prefer headerData, fall back to legacy Redux state
  const profileImage = companyLogo || reduxProfileImage || Logo;
  const displayName = companyName || reduxProfileName || email || 'N/A';

  return (
    <div className="Header_Main">
      <header className="Header_Container">
        {/* Dynamic Page Title on Left */}
        <h4 className="Header_Page_Title">{pageTitle}</h4>

        {/* Right Section */}
        <div className="Header_Right_Section">
          {/* Location Pill Badge */}
          <div className="Header_Location_Pill">
            <MapPin size={16} />
            <CurrentLocation lat={undefined} lng={undefined} />
          </div>

          {/* Notification Icon Badge */}
          <div className="position-relative">
            <div
              className="Header_Notification_Circle"
              onClick={() => {
                const nextOpen = !open;
                setOpen(nextOpen);
                if (nextOpen) {
                  refreshNotifications();
                }
              }}
              title="Notifications"
            >
              <HiOutlineBell size={18} />
              {unreadCount > 0 && <span className="Header_Notification_Badge_Dot" />}
            </div>

            {/* Notifications Dropdown Preview */}
            {open && (
              <div
                ref={notificationRef}
                className="Header_Notifications_Dropdown"
              >
                <NotificationsPreview
                  notifications={notifications}
                  showCount={5}
                  loading={notificationsLoading}
                  onReadAll={() => markRead(0)}
                  onRead={(id) => markRead(id)}
                  setOpen={() => setOpen(false)}
                  onViewAll={() => {
                    setOpen(false);
                    navigate("/notifications");
                  }}
                />
              </div>
            )}
          </div>

          {/* Profile Section Dropdown */}
          <Dropdown align="end" className="Header_Profile_Dropdown">
            <Dropdown.Toggle
              className="Header_Dropdown_Toggle"
            >
              <div className="Header_Profile_Container">
                <img
                  src={profileImage}
                  alt="Profile"
                  className="Header_Profile_Avatar"
                  onError={(e) => { e.currentTarget.src = Logo; }}
                />
                <div className="Header_Profile_Meta">
                  <span className="Header_Profile_Name">{displayName}</span>
                  <span className="Header_Profile_Email">{email}</span>
                </div>
                <ChevronDown size={14} className="Header_Profile_Arrow" />
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="Header_Dropdown_Menu">
              {/* Profile Header */}
              <div className="Header_Menu_Profile_Header">
                <img
                  src={profileImage}
                  alt="Profile"
                  className="Header_Menu_Avatar"
                  onError={(e) => { e.currentTarget.src = Logo; }}
                />
                <div className="Header_Menu_Profile_Info">
                  <h6 className="Header_Menu_Name">{displayName}</h6>
                  <span className="Header_Menu_Email">{email}</span>
                </div>
              </div>

              {/* Status */}
              <div className="Header_Menu_Status_Row">
                <span className="Header_Status_Badge">
                  <span className="Header_Status_Dot" />
                  Active Account
                </span>
              </div>

              {/* Menu Items */}
              <div className="Header_Menu_Section">
                <Dropdown.Item
                  onClick={() => navigate("/company-profile")}
                  className="Header_Menu_Item"
                >
                  <User size={15} /> Company Profile
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={() => navigate("/personal-profile")}
                  className="Header_Menu_Item"
                >
                  <User size={15} /> Personal Profile
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={() => navigate("/settings")}
                  className="Header_Menu_Item"
                >
                  <Settings size={15} /> Account Settings
                </Dropdown.Item>
              </div>

              {/* Logout */}
              <div className="Header_Menu_Footer">
                <Dropdown.Item
                  onClick={handleLogout}
                  className="Header_Menu_Item Header_Menu_Item_Logout"
                >
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_3158_757)">
                                    <path d="M19.4764 8.6514L17.3584 6.53343C17.1854 6.36515 16.9531 6.27176 16.7117 6.27346C16.4703 6.27516 16.2393 6.37182 16.0687 6.54252C15.898 6.71322 15.8014 6.94424 15.7998 7.1856C15.7982 7.42697 15.8917 7.65927 16.06 7.83226L17.199 8.97132H12.7967C12.6739 8.96774 12.5516 8.98883 12.4371 9.03335C12.3226 9.07787 12.2182 9.14492 12.1301 9.23051C12.042 9.31611 11.9719 9.41851 11.9241 9.53167C11.8762 9.64483 11.8516 9.76643 11.8516 9.88929C11.8516 10.0121 11.8762 10.1338 11.9241 10.2469C11.9719 10.3601 12.042 10.4625 12.1301 10.5481C12.2182 10.6337 12.3226 10.7007 12.4371 10.7452C12.5516 10.7897 12.6739 10.8108 12.7967 10.8073H17.2401L16.101 11.9467C16.0157 12.032 15.9481 12.1332 15.902 12.2446C15.8558 12.356 15.8321 12.4754 15.8321 12.5959C15.8321 12.7165 15.8558 12.8359 15.902 12.9473C15.9481 13.0587 16.0157 13.1599 16.101 13.2451C16.2732 13.4173 16.5067 13.5141 16.7502 13.5141C16.8708 13.5141 16.9902 13.4903 17.1016 13.4442C17.213 13.398 17.3142 13.3304 17.3994 13.2451L19.4764 11.1682C19.6417 11.0029 19.7729 10.8067 19.8623 10.5907C19.9518 10.3747 19.9978 10.1432 19.9978 9.90941C19.9978 9.67562 19.9518 9.44413 19.8623 9.22815C19.7729 9.01216 19.6417 8.81592 19.4764 8.65062V8.6514Z" fill="#E77469" />
                                    <path d="M14.4053 11.9836C14.1717 11.9876 13.9478 12.0778 13.7767 12.237C13.6057 12.3962 13.4995 12.613 13.4788 12.8457C13.4448 13.293 13.4108 13.4434 13.3686 13.7766C13.3074 14.2227 13.1015 14.6364 12.7827 14.9543C12.4642 15.2774 12.0494 15.4883 11.6006 15.5551L11.5741 15.5586C11.3006 15.5977 11.0467 15.632 10.7823 15.6633C10.8338 14.6367 10.8666 13.45 10.8748 12.2723C10.8897 10.3246 10.8358 8.31523 10.7291 7.01836C10.6493 6.1115 10.3801 5.23149 9.93891 4.43516C9.50258 3.65078 8.93539 3.03437 8.34086 2.7082L8.3225 2.69844C7.80414 2.41484 7.36508 2.17461 6.60727 1.85508C6.92992 1.84453 7.24399 1.83789 7.66196 1.83789C9.72172 1.84609 10.4506 1.96992 11.4635 2.12266L11.4885 2.12656C11.9347 2.20266 12.3446 2.42028 12.6577 2.74727C12.9756 3.07141 13.1832 3.48768 13.2506 3.93672C13.3588 4.72148 13.4459 5.61641 13.4983 6.9082C13.5174 7.39492 13.928 7.79219 14.4272 7.80391C14.9405 7.81562 15.3471 7.42539 15.3237 6.93633C15.2635 5.66602 15.1639 4.72539 15.0338 3.87617C14.9061 3.04531 14.4823 2.23164 13.858 1.58164C13.2338 0.931641 12.4397 0.480469 11.6123 0.335547L11.587 0.33125C10.5768 0.163281 9.78227 0.0128906 7.66508 0.00195312C5.54828 0.0144531 4.80375 0.1625 3.79203 0.329688L3.76664 0.334375C2.9393 0.478906 2.14321 0.93125 1.51821 1.58008C0.893205 2.22891 0.470158 3.04375 0.342424 3.87461C0.152971 5.09766 0.0158615 7.0125 0.00140833 8.8832C-0.0130448 10.7539 0.0846115 12.6664 0.254533 13.8855C0.330221 14.4049 0.510841 14.9035 0.785393 15.3508C0.861565 15.507 0.943596 15.6633 1.03149 15.8125C1.48735 16.5902 2.07289 17.2211 2.67602 17.575L2.69438 17.5859C3.42953 18.0156 4.01235 18.3629 5.53149 19.0117C7.04633 19.6523 7.6018 19.7812 8.31 19.9492L8.32758 19.9535C8.90649 20.0883 9.45297 19.9023 9.87641 19.4395C10.2866 18.991 10.5635 18.3035 10.6538 17.5023C11.0252 17.4582 11.3569 17.4082 11.7159 17.3523L11.7416 17.348C12.585 17.2164 13.3928 16.7809 14.0194 16.1469C14.6459 15.5129 15.0616 14.7098 15.176 13.8813C15.2252 13.5227 15.2635 13.3465 15.3018 12.8859C15.3436 12.3848 14.9319 11.973 14.4053 11.9836Z" fill="#E77469" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_3158_757">
                                        <rect width="20" height="20" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                  Logout
                </Dropdown.Item>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      <LogoutModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmLogout}
        loading={loading}
      />
    </div>
  );
};

export default Header;
